import Faye from 'faye';

/**
 * FayeClient - Handles all real-time messaging via Faye pub/sub
 * This replaces the old Backbone-based Faye client with a clean Promise-based API
 */
class FayeService {
  constructor() {
    this.client = null;
    this.subscriptions = new Map();
    this.messageStack = {};
    this.currentUsername = null;
  }

  /**
   * Initialize and connect to Faye server
   */
  connect() {
    if (this.client) return this.client;

    this.client = new Faye.Client('/faye', {
      timeout: 120,
      retry: 5
    });

    console.log('[FayeClient] Connected to Faye server');
    return this.client;
  }

  /**
   * Generate a unique message ID (UUID v4)
   */
  generateUUID() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  /**
   * Subscribe to a channel and call callback when messages arrive.
   * Multiple callbacks can share one underlying Faye subscription per
   * channel; each callback is invoked in its own try/catch so one
   * throwing listener can't starve the others on the same channel.
   */
  subscribe(channel, callback) {
    if (!this.client) this.connect();

    let entry = this.subscriptions.get(channel);
    if (!entry) {
      const callbacks = new Set();
      const subscription = this.client.subscribe(channel, (message) => {
        callbacks.forEach(cb => {
          try {
            cb(message);
          } catch (error) {
            console.error(`[FayeClient] Subscriber error on channel ${channel}:`, error);
          }
        });
      });
      entry = { subscription, callbacks };
      this.subscriptions.set(channel, entry);
    }
    entry.callbacks.add(callback);

    return entry.subscription;
  }

  /**
   * Unsubscribe a specific callback from a channel. The underlying Faye
   * subscription is only cancelled once the last callback leaves.
   */
  unsubscribe(channel, callback) {
    const entry = this.subscriptions.get(channel);
    if (!entry) return;

    entry.callbacks.delete(callback);
    if (entry.callbacks.size === 0) {
      entry.subscription.cancel();
      this.subscriptions.delete(channel);
    }
  }

  /**
   * Publish a message to a channel (fire-and-forget)
   */
  publish(channel, data) {
    if (!this.client) this.connect();

    // Add timestamp to all messages
    data.Date = new Date().toISOString();

    return this.client.publish(channel, data);
  }

  /**
   * Send a message to Faye and wait for response (request-response pattern)
   * This is used for fetching data, not for updates
   *
   * @param {string} requestChannel - Channel to send request to
   * @param {object} msg - Message payload
   * @param {string} responseChannelBase - Base channel to listen for response
   * @returns {Promise} - Resolves with response data
   */
  sendToFaye(requestChannel, msg, responseChannelBase) {
    return new Promise((resolve, reject) => {
      if (!this.client) this.connect();

      // Generate unique message ID
      const messageGuid = this.generateUUID();
      const responseChannel = responseChannelBase + (responseChannelBase.endsWith('/') ? '' : '/') + messageGuid;
      msg.response_channel = responseChannel;

      // Track this message
      this.messageStack[messageGuid] = {
        messageGuid,
        resolve,
        reject
      };

      // Set up response handler
      const handleResponse = (responseMessage) => {
        const msgStack = this.messageStack[messageGuid];
        if (msgStack) {
          this.unsubscribe(responseChannel, handleResponse);
          delete this.messageStack[messageGuid];

          try {
            // Check for server-side error response
            if (responseMessage.status === 'error') {
              msgStack.reject(new Error(responseMessage.error || 'Server rejected the request'));
              return;
            }

            // Parse the response data
            let docs;
            if (responseMessage.docs != null) {
              docs = JSON.parse(responseMessage.docs, this.dateTimeReviver);
            } else if (responseMessage.doc != null) {
              docs = JSON.parse(responseMessage.doc, this.dateTimeReviver);
            } else {
              docs = responseMessage;
            }

            msgStack.resolve({ ...responseMessage, docs });
          } catch (error) {
            msgStack.reject(error);
          }
        }
      };

      // Set timeout (60 seconds)
      setTimeout(() => {
        const msgStack = this.messageStack[messageGuid];
        if (msgStack) {
          this.unsubscribe(responseChannel, handleResponse);
          delete this.messageStack[messageGuid];
          msgStack.reject(new Error('Request timeout: No server response within 60 seconds'));
        }
      }, 60000);

      // Subscribe to response channel
      this.subscribe(responseChannel, handleResponse);

      // Publish the request
      this.client.publish(requestChannel, msg);
    });
  }

  /**
   * Date/time reviver for JSON.parse
   * Converts ISO date strings back to Date objects
   */
  dateTimeReviver(key, value) {
    if (typeof value === 'string') {
      const match = /000Z$/.exec(value);
      if (match) {
        return new Date(value);
      }
    }
    return value;
  }

  /**
   * Set the current username for all messages
   */
  setCurrentUsername(username) {
    this.currentUsername = username;
  }

  /**
   * Disconnect from Faye server and clean up
   */
  disconnect() {
    if (this.client) {
      // Cancel all subscriptions
      this.subscriptions.forEach(entry => entry.subscription.cancel());
      this.subscriptions.clear();

      // Reject all pending messages
      Object.values(this.messageStack).forEach(msgStack => {
        msgStack.reject(new Error('Faye client disconnected'));
      });
      this.messageStack = {};

      // Disconnect client
      this.client.disconnect();
      this.client = null;

      console.log('[FayeClient] Disconnected from Faye server');
    }
  }
}

// Export a singleton instance
export default new FayeService();
